import { SupabaseClient } from '@supabase/supabase-js';
import { Database } from '../lib/database.types';
import { supabase } from '../lib/supabase';

export type ComparativeMetric = {
  metricName: string;
  userValue: number | null;
  surveyMin: number | null;
  surveyMax: number | null;
  surveyMedian: number | null;
  surveyAverage: number | null;
  brandAverage: number | null;
  countyAverage: number | null;
};

export type ComparativeAnalysis = {
  id: string;
  userId: string;
  valuationId: string;
  propertyId: string;
  metrics: ComparativeMetric[];
  createdAt: string;
};

type OpCompRow = Database['public']['Tables']['OpComps']['Row'];

const comparativeAnalysisTable = supabase.client.from('ComparativeAnalysis');
const opCompsTable = supabase.client.from('OpComps');

/**
 * Generate comparative analysis based on valuation data and OpComps survey data
 * @param userId User ID
 * @param valuationId Valuation ID
 * @param propertyId Property ID
 * @param propertyData Property information
 * @param valuationMetrics Key-value pairs of valuation metrics
 */
export async function generateComparativeAnalysis(
  userId: string,
  valuationId: string,
  propertyId: string,
  propertyData: {
    fuelBrand?: string;
    county?: string;
  },
  valuationMetrics: {
    fuelVolume: number;
    storeRevenue: number;
    operatingExpenses: number;
    siteSize: number;
    trafficCount: number;
    yearBuilt: number;
  }
): Promise<ComparativeAnalysis | null> {
  try {
    // Try to fetch OpComps data for comparison, but handle missing table gracefully
    let opComps: OpCompRow[] = [];
    
    try {
      const { data: opCompsData, error: fetchError } = await opCompsTable.select('*');
      
      if (fetchError) {
        console.warn('OpComps table not available, skipping comparative analysis:', fetchError.message);
        // Continue without comparative data - this is not a critical failure
      } else if (opCompsData) {
        opComps = opCompsData;
      }
    } catch (tableError) {
      console.warn('OpComps table does not exist, skipping comparative analysis');
      // Continue without comparative data
    }

    // Process each metric and calculate statistics
    const metrics: ComparativeMetric[] = [];

    for (const metricName in valuationMetrics) {
      const userValue = valuationMetrics[metricName as keyof typeof valuationMetrics];
      
      // Filter opComps by brand if available
      let brandFiltered: OpCompRow[] = [];
      if (propertyData.fuelBrand) {
        brandFiltered = opComps.filter((opComp: OpCompRow) => 
          opComp.Fuel_Brand?.toLowerCase() === propertyData.fuelBrand?.toLowerCase()
        );
      }
      
      // Filter opComps by county if available
      let countyFiltered: OpCompRow[] = [];
      if (propertyData.county) {
        countyFiltered = opComps.filter((opComp: OpCompRow) => 
          opComp.County?.toLowerCase() === propertyData.county?.toLowerCase()
        );
      }
      
      // Calculate survey stats - Min/Max
      const surveyValues = opComps
        .map((opComp: OpCompRow) => opComp[metricName as keyof OpCompRow] as number | null)
        .filter((value): value is number => value !== null && !isNaN(value));
      
      const surveyMin = surveyValues.length ? Math.min(...surveyValues) : null;
      const surveyMax = surveyValues.length ? Math.max(...surveyValues) : null;
      
      // Calculate survey median
      let surveyMedian: number | null = null;
      if (surveyValues.length > 0) {
        const sorted = [...surveyValues].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        surveyMedian = sorted.length % 2 !== 0 
          ? sorted[mid] 
          : (sorted[mid - 1] + sorted[mid]) / 2;
      }
      
      // Calculate survey average
      const surveyAverage = surveyValues.length 
        ? surveyValues.reduce((sum: number, val: number) => sum + val, 0) / surveyValues.length 
        : null;
      
      // Calculate brand average if available
      let brandAverage: number | null = null;
      if (brandFiltered.length > 0) {
        const brandValues = brandFiltered
          .map((opComp: OpCompRow) => opComp[metricName as keyof OpCompRow] as number | null)
          .filter((value): value is number => value !== null && !isNaN(value));
        
        if (brandValues.length > 0) {
          brandAverage = brandValues.reduce((sum: number, val: number) => sum + val, 0) / brandValues.length;
        }
      }
      
      // Calculate county average if available
      let countyAverage: number | null = null;
      if (countyFiltered.length > 0) {
        const countyValues = countyFiltered
          .map((opComp: OpCompRow) => opComp[metricName as keyof OpCompRow] as number | null)
          .filter((value): value is number => value !== null && !isNaN(value));
        
        if (countyValues.length > 0) {
          countyAverage = countyValues.reduce((sum: number, val: number) => sum + val, 0) / countyValues.length;
        }
      }
      
      // Add metric to results
      metrics.push({
        metricName,
        userValue,
        surveyMin,
        surveyMax,
        surveyMedian,
        surveyAverage,
        brandAverage,
        countyAverage
      });

      // Store the analysis in the database
      await comparativeAnalysisTable.upsert({
        user_id: userId,
        valuation_id: valuationId,
        property_id: propertyId,
        metric_name: metricName,
        user_value: userValue,
        survey_min: surveyMin,
        survey_max: surveyMax,
        survey_median: surveyMedian,
        survey_average: surveyAverage,
        brand_average: brandAverage,
        county_average: countyAverage
      }, {
        onConflict: 'valuation_id,metric_name'
      });
    }

    // Return the full analysis
    return {
      id: `${valuationId}-analysis`,
      userId,
      valuationId,
      propertyId,
      metrics,
      createdAt: new Date().toISOString()
    };
    
  } catch (error) {
    console.error('Error generating comparative analysis:', error);
    return null;
  }
}

/**
 * Fetch existing comparative analysis for a valuation
 */
export async function fetchComparativeAnalysis(
  valuationId: string
): Promise<ComparativeAnalysis | null> {
  try {
    const { data: analysisRecords, error } = await comparativeAnalysisTable
      .select('*')
      .eq('valuation_id', valuationId)
      .order('created_at', { ascending: false });

    if (error || !analysisRecords || analysisRecords.length === 0) {
      return null;
    }

    // Group metrics by valuation and take the first record for basic info
    const firstRecord = analysisRecords[0];
    
    // Convert individual metric records to ComparativeMetric array
    const metrics: ComparativeMetric[] = analysisRecords.map(record => ({
      metricName: record.metric_name,
      userValue: record.user_value,
      surveyMin: record.survey_min,
      surveyMax: record.survey_max,
      surveyMedian: record.survey_median,
      surveyAverage: record.survey_average,
      brandAverage: record.brand_average,
      countyAverage: record.county_average,
    }));

    // Convert from database format to app format
    const analysis: ComparativeAnalysis = {
      id: firstRecord.id,
      userId: firstRecord.user_id,
      valuationId: firstRecord.valuation_id,
      propertyId: firstRecord.property_id,
      metrics: metrics,
      createdAt: firstRecord.created_at
    };

    return analysis;
  } catch (error) {
    console.error('Error fetching comparative analysis:', error);
    return null;
  }
}
